import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { customerInterface } from 'src/app/util/interfaces/customer';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { environment } from 'src/environments/environment';
import { categoryFormInterface, questionFormInterface } from '../service-catalog.component';

@Injectable({
	providedIn: 'root',
})
export class ServiceCatalogService {
	headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	private getCustomerInfoUrl = environment.baseApiUrl + 'customer/{idCustomer}';
	private getAvailableTemplatesUrl = environment.baseApiUrl + 'register/selfserviceCatalog/template';
	private getAvailableKbsUrl = environment.baseApiUrl + 'register/selfserviceCatalog/kb';

	private getSelfserviceCatalogCategoriesUrl = environment.baseApiUrl + 'register/selfserviceCatalog/category';
	private postSelfserviceCatalogCategoriesUrl = environment.baseApiUrl + 'register/selfserviceCatalog/category';
	private putSelfserviceCatalogCategoriesUrl = environment.baseApiUrl + 'register/selfserviceCatalog/category';
	private deleteSelfserviceCatalogCategoriesUrl = environment.baseApiUrl + 'register/selfserviceCatalog/category';

	private getSelfserviceCatalogQuestionsUrl = environment.baseApiUrl + 'register/selfserviceCatalog/question';
	private postSelfserviceCatalogQuestionsUrl = environment.baseApiUrl + 'register/selfserviceCatalog/question';
	private putSelfserviceCatalogQuestionsUrl = environment.baseApiUrl + 'register/selfserviceCatalog/question';
	private deleteSelfserviceCatalogQuestionsUrl = environment.baseApiUrl + 'register/selfserviceCatalog/question';

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService
	) {}

	getCustomerInfo(idCustomer: string): Observable<customerInterface> {
		const getCustomerInfoUrl = this.getCustomerInfoUrl.replace('{idCustomer}', idCustomer);
		return this.http.get<customerInterface>(getCustomerInfoUrl, { headers: this.headers });
	}

	getCategory(): Observable<categoryFormInterface[]> {
		return this.http.get<categoryFormInterface[]>(this.getSelfserviceCatalogCategoriesUrl, { headers: this.headers }).pipe(
			map((catalogCategoryRes) => {
				for (const category of catalogCategoryRes) {
					category.icon = this.formatIcons(category.icon);
				}
				return catalogCategoryRes;
			})
		);
	}

	formatIcons(iconText: string): string {
		if (iconText) {
			let newIconText = '';
			if (iconText.includes('-')) {
				newIconText += iconText.split('-')[1];
			} else {
				newIconText = iconText;
			}

			return newIconText;
		}

		return null;
	}

	postCategory(categories: categoryFormInterface[]): Observable<string[]> {
		if (categories.length === 0) return of([]);
		return this.http.post<string[]>(this.postSelfserviceCatalogCategoriesUrl, categories, { headers: this.headers });
	}

	putCategory(categories: categoryFormInterface[]): Observable<any> {
		if (categories.length === 0) return of([]);
		return this.http.put<any>(this.putSelfserviceCatalogCategoriesUrl, categories, { headers: this.headers });
	}

	deleteCategory(idCategories: string[]): Observable<any> {
		if (idCategories.length === 0) return of([]);
		const qryString = `?id=${idCategories.join(',')}`;
		const deleteSelfserviceCatalogCategoriesUrl = this.deleteSelfserviceCatalogCategoriesUrl + qryString;
		return this.http.delete<any>(deleteSelfserviceCatalogCategoriesUrl, { headers: this.headers });
	}

	//Answer crud responsibility is in question crud
	getQuestion(): Observable<questionFormInterface[]> {
		return this.http.get<any[]>(this.getSelfserviceCatalogQuestionsUrl, { headers: this.headers }).pipe(
			map((catalogQuestionRes) => {
				for (const question of catalogQuestionRes) {
					if (question.selfservice_category) {
						question.type_predecessor = 'category';
						question.id_predecessor = question.selfservice_category;
					} else {
						question.type_predecessor = '';
						question.id_predecessor = undefined;
					}
					for (const answer of question.answer) {
						answer.icon = this.formatIcons(answer.icon);
						if (answer.question) answer.goTo = 'question';
						if (answer.externUrl) answer.goTo = 'externUrl';
						if (answer.template) answer.goTo = 'template';
						if (answer.kb) answer.goTo = 'kb';
						if (!answer.goTo) answer.goTo = '';
					}
				}
				return catalogQuestionRes;
			})
		);
	}

	postQuestion(questions: questionFormInterface[]): Observable<questionFormInterface[]> {
		if (questions.length === 0) return of([]);
		return this.http.post<questionFormInterface[]>(this.postSelfserviceCatalogQuestionsUrl, questions, { headers: this.headers });
	}

	putQuestion(questions: questionFormInterface[]): Observable<any> {
		if (questions.length === 0) return of([]);
		return this.http.put<any>(this.putSelfserviceCatalogQuestionsUrl, questions, { headers: this.headers });
	}

	deleteQuestion(idQuestions: string[]): Observable<any> {
		if (idQuestions.length === 0) return of([]);
		const qryString = `?id=${idQuestions.join(',')}`;
		const deleteSelfserviceCatalogQuestionsUrl = this.deleteSelfserviceCatalogQuestionsUrl + qryString;
		return this.http.delete<any>(deleteSelfserviceCatalogQuestionsUrl, { headers: this.headers });
	}

	getAvailableTemplates(): Observable<{ id: string; name: string }[]> {
		return this.http.get(this.getAvailableTemplatesUrl, { headers: this.headers }).pipe(
			map((templates: any[]) => {
				const newTemplateArray: { id: string; name: string }[] = [];
				for (const template of templates) {
					const newTemplate = {
						id: template.id,
						name: template.name,
					};

					newTemplateArray.push(newTemplate);
				}

				return newTemplateArray;
			})
		);
	}

	getAvailableKbs(): Observable<{ id: string; name: string }[]> {
		return this.http.get(this.getAvailableKbsUrl, { headers: this.headers }).pipe(
			map((kbs: any[]) => {
				const newKbArray: { id: string; name: string }[] = [];
				for (const kb of kbs) {
					const newKb = {
						id: kb.id,
						name: kb.summary,
					};

					newKbArray.push(newKb);
				}

				return newKbArray;
			})
		);
	}
}
